import { createNavigationContainerRef, type NavigationState, type PartialState } from '@react-navigation/native';
import type { ScreenParameters } from 'app/types/navigation';

export const navigationRef = createNavigationContainerRef<ScreenParameters>();

interface PendingNavigationStateSubscription {
  onStoreChange: () => void;
  unsubscribe?: () => void;
}

const pendingNavigationStateSubscriptions = new Set<PendingNavigationStateSubscription>();

interface RouteBranch {
  key?: string;
  name: string;
  state?: NavigationState | PartialState<NavigationState>;
}

export function subscribeNavigationState(onStoreChange: () => void) {
  if (!navigationRef.isReady()) {
    const subscription: PendingNavigationStateSubscription = { onStoreChange };
    pendingNavigationStateSubscriptions.add(subscription);
    return () => {
      pendingNavigationStateSubscriptions.delete(subscription);
      subscription.unsubscribe?.();
    };
  }
  return navigationRef.addListener('state', onStoreChange);
}

export function notifyNavigationReady() {
  for (const subscription of pendingNavigationStateSubscriptions) {
    pendingNavigationStateSubscriptions.delete(subscription);
    subscription.unsubscribe = navigationRef.addListener('state', subscription.onStoreChange);
    subscription.onStoreChange();
  }
}

export function getCurrentRouteBranchIdentity() {
  if (!navigationRef.isReady()) {
    return '';
  }

  const routeKeys: string[] = [];
  let state: NavigationState | PartialState<NavigationState> | undefined = navigationRef.getRootState();

  while (state?.routes.length) {
    const route: RouteBranch | undefined = state.routes[state.index ?? 0];
    if (!route) break;

    routeKeys.push(`${route.name}:${route.key ?? route.name}`);
    state = route.state;
  }

  return routeKeys.join('>');
}

export function getCurrentRoutePath(): string | undefined {
  if (!navigationRef.isReady()) {
    return undefined;
  }

  const routeNames: string[] = [];
  let state: NavigationState | PartialState<NavigationState> | undefined = navigationRef.getRootState();

  while (state?.routes.length) {
    const route: RouteBranch | undefined = state.routes[state.index ?? 0];
    if (!route) break;

    routeNames.push(route.name);
    state = route.state;
  }

  return routeNames.join('/');
}

export function getCurrentScreenName(): keyof ScreenParameters | undefined {
  if (!navigationRef.isReady()) {
    return undefined;
  }
  return navigationRef.getCurrentRoute()?.name;
}
